import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { XCircle, Loader2, AlertTriangle } from 'lucide-react';
import { ProductionImpactDisclosure } from './ProductionImpactDisclosure';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const t = localStorage.getItem('mosaic_session_token') || '';
  return { headers: { Authorization: `Bearer ${t}` } };
};

/**
 * v6.32.12 — Surfaces a "Cancel stuck audit" control inside the running
 * banner when `elapsed > 90 min` (v6.34.3 — bumped from 30 min to fit the
 * current 110-scene workload at ~30s/scene = ~55 min normal duration).
 * A normal BP audit completes in ~50-60 min; if it's still showing
 * "running" after 90 min, the backend process was almost certainly killed
 * (deploy rollout, ingress event, OOM) and the DB doc is now a ghost
 * that blocks every downstream workflow.
 *
 * Renders a compact inline button inside the indigo banner. On click, opens
 * a confirm dialog for the Super Admin password, hits
 * POST /api/bp-auditor/cancel-stuck, and triggers the parent to re-fetch.
 */
const CancelStuckAuditButton = ({ onCancelled }) => {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);

  const doCancel = async () => {
    if (!pw) { toast.error('Password required'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/bp-auditor/cancel-stuck`,
        { password: pw },
        { ...authCfg(), timeout: 20000 },
      );
      if (data.cancelled > 0) {
        toast.success(`Cancelled ${data.cancelled} stuck audit${data.cancelled === 1 ? '' : 's'}. Refreshing…`);
      } else {
        toast.info('No stuck audit found (elapsed may not yet exceed the 90-minute threshold on the server).');
      }
      setOpen(false);
      setPw('');
      if (onCancelled) onCancelled();
    } catch (e) {
      toast.error(`Cancel failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-indigo-200 flex items-center gap-3 flex-wrap">
      <AlertTriangle size={14} className="text-amber-600 flex-shrink-0" />
      <span className="text-xs text-amber-800 font-medium">
        This audit has been running for more than 90 minutes — longer than a normal run (~50-60 min for 110 scenes).
      </span>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="border-rose-300 text-rose-700 hover:bg-rose-50"
        data-testid="bp-audit-cancel-stuck-btn"
      >
        <XCircle size={14} className="mr-1" />
        Cancel stuck audit
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-rose-600" />
              Cancel stuck audit?
            </DialogTitle>
            <DialogDescription>
              Marks the stalled audit record as <code>failed</code> so the UI stops showing a ghost "in progress" banner
              and you can start a fresh audit. Any scenes that were audited before the interruption are discarded —
              start a new audit if you need a fresh report.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              type="password"
              value={pw}
              onChange={e => setPw(e.target.value)}
              placeholder="Super Admin password"
              data-testid="bp-audit-cancel-stuck-password"
            />
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
              Only cancels runs whose <code>started_at</code> is more than <strong>90 minutes</strong> ago.
              A normal 110-scene audit completes in ~50-60 min, so this threshold is safe — we won't kill a still-working worker.
            </div>
            <ProductionImpactDisclosure
              testId="bp-audit-cancel-stuck-disclosure"
              removes={[
                <><code className="bg-gray-50 px-1">bp_audit_runs</code> docs with <code className="bg-gray-50 px-1">status='running'</code> AND <code className="bg-gray-50 px-1">started_at &lt; now-90min</code> are flipped to <code className="bg-gray-50 px-1">status='failed'</code> with a <code className="bg-gray-50 px-1">failure_reason</code> + <code className="bg-gray-50 px-1">cancelled_by</code> stamp</>,
                <>One <code className="bg-gray-50 px-1">audit_logs</code> row inserted recording the cancellation (forensic traceability)</>,
              ]}
              doesNotTouch={[
                <>Audit results that completed before the interruption — they remain queryable in <code className="bg-gray-50 px-1">bp_audit_runs</code></>,
                <>Big Picture image library (<code className="bg-gray-50 px-1">/bp_images/</code>) and candidates (<code className="bg-gray-50 px-1">/bp_review/</code>)</>,
                <>Question bank (<code className="bg-gray-50 px-1">questions</code> collection) — no <code className="bg-gray-50 px-1">bp_image_url</code> fields change</>,
                <>Any other audits (text-fix, visual-fix, content-auditor) — only BP scene audits are affected</>,
                <>Student data, exam state, in-progress assessments, sessions</>,
              ]}
              note="If you need to re-audit, click Run Audit Now after this completes — a fresh run will start clean."
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} data-testid="bp-audit-cancel-stuck-cancel">
              Keep waiting
            </Button>
            <Button
              onClick={doCancel}
              disabled={busy || !pw}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="bp-audit-cancel-stuck-confirm"
            >
              {busy
                ? <><Loader2 size={14} className="mr-1 animate-spin" />Cancelling…</>
                : 'Cancel stuck audit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CancelStuckAuditButton;
